 
import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Box,
  Grid,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import {
  Add,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';

export interface Columns {
  header: string;
  accessor: string;
   
  cell?: (value: any, row?: any) => React.JSX.Element;
  align?: 'left' | 'right' | 'center';
  isSortEnabled?: boolean;
  hideColumn?: boolean;
}

export interface MatrixTableProps {
  columns: Columns[];
  rowsData: any[];
   
  handleSort: (fieldName: string) => void;
  sortConfig: {
    fieldName: string;
    direction: 'asc' | 'desc' | null;
  };
  handleAddMore?: () => void;
  showAddMore?: boolean;
  tableHeight?: string;
  groupByCategory?: boolean;
}

const MatrixTable: React.FC<MatrixTableProps> = ({
  columns,
  rowsData,
  handleAddMore,
  handleSort,
  sortConfig,
  showAddMore,
  tableHeight,
  groupByCategory = false,
}) => {
  const useStyles = makeStyles()({
    paper: {
      borderRadius: '6px',
      boxShadow: '0px 2px 4px 0px #0000000D',
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      maxHeight: tableHeight ?? '500px',
      ...scrollBarStyles,
      '&::-webkit-scrollbar': {
        width: '4px',
      },
    },
    neutral: { backgroundColor: patientAccessTheme.palette.status.white },
    favorable: { backgroundColor: '#08C7B41A' },
    unfavorable: { backgroundColor: '#EDC0BB4D' },
    headerRow: {
      background: patientAccessTheme.palette.grey[100],
      height: '60px',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    headerCellStyles: {
      fontSize: '14px',
      color: patientAccessTheme.palette.text.highemphasis,
      lineHeight: '19.2px',
      fontWeight: 500,
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
      borderTop: 'none',
      minWidth: '150px',
    },
    rowCellStyles: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '22.4px',
      color: patientAccessTheme.palette.text.mediumemphasis,
      border: `1px solid ${patientAccessTheme.palette.grey[300]}`,
    },
    categoryRow: {
      backgroundColor: patientAccessTheme.palette.grey[700],
    },
    clauseRow: {
      backgroundColor: patientAccessTheme.palette.grey[400],
    },
    categoryCell: {
      fontWeight: 500,
      color: patientAccessTheme.palette.status.white,
      fontSize: '14px',
      padding: '8px 0px 8px 20px',
    },
    clauseCell: {
      fontWeight: 500,
      color: '#000 !important',
      fontSize: '14px',
      padding: '8px 0px 8px 20px',
    },
    buttonGrid: {
      display: 'flex',
      position: 'absolute',
      top: '-11px',
      right: '-8px',
      '&:hover $buttonHoverInfo': { visibility: 'visible' },
    },
    addButton: {
      borderRadius: '50px',
      background: patientAccessTheme.palette.primary[500],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      zIndex: 10,
      cursor: 'pointer',
    },
    buttonHoverInfo: {
      visibility: 'hidden',
      borderRadius: '50px',
      color: 'transparent',
      width: '201px',
      height: '24px',
      position: 'absolute',
      top: '0px',
      right: '0px',
      zIndex: 2,
      padding: '3px 18px',
      background: patientAccessTheme.palette.primary[400],
    },
    addIcon: { marginLeft: '1px', width: '12px', height: '12px' },
  });

  const { classes } = useStyles();

  const visibleColumns = columns?.filter((column) => !column?.hideColumn);

  const groupedRows = React.useMemo(() => {
    if (!groupByCategory) {
      return null;
    }
    const contractColumns = visibleColumns
      .map((col) => col.accessor)
      .filter(
        (accessor) =>
          ![
            'name',
            'description',
            'position',
            'key',
            'favorability_range',
            '_id',
          ].includes(accessor),
      );

    const filteredRows = rowsData.filter((row: any) =>
      contractColumns.every((accessor) => {
        const contractData = row[accessor];

        if (!contractData || contractData.response_type !== 'BOOLEAN') {
          return true;
        }

        return contractData.value === true;
      }),
    );

    return filteredRows.reduce((acc: any, row: any) => {
      const paramCategory = row.paramCategory || 'Non-categorized';
      const category = row.category || 'Others';

      if (!acc[paramCategory]) {
        acc[paramCategory] = {};
      }
      if (!acc[paramCategory][category]) {
        acc[paramCategory][category] = [];
      }

      acc[paramCategory][category].push(row);

      return acc;
    }, {});
  }, [rowsData, groupByCategory, visibleColumns]);
  const AddButton = () => {
    return (
      <Grid className={classes.buttonGrid}>
        <div className={classes.buttonHoverInfo}>
          <Typography
            textColor={patientAccessTheme.palette.status.white}
            fontType="medium"
            fontSize="11px"
            lineHeight="17.6px"
          >
            Add a group for comparison
          </Typography>
        </div>
        <div className={classes.addButton} onClick={handleAddMore}>
          <Add className={classes.addIcon} />
        </div>
      </Grid>
    );
  };

  const renderRow = (row: any, rowIndex: string) => {
    return (
      <TableRow key={rowIndex}>
        {visibleColumns?.map((column, colIndex: number) => {
          const cellContent = column?.cell
            ? column?.cell(row[column?.accessor], row)
            : row[column?.accessor];

          const cellTooltip = row?.tooltip?.[column?.accessor] || null;

          const cellBackground =
            row?.background?.[column?.accessor] ||
            patientAccessTheme.palette.status.white;

          const tableCell = (
            <TableCell
              key={colIndex}
              className={classes.rowCellStyles}
              align={column?.align}
              style={{
                background: cellBackground,
              }}
            >
              {cellContent}
            </TableCell>
          );

          return cellTooltip ? (
            <Tooltip
              key={colIndex}
              title={
                typeof cellTooltip === 'object' &&
                !Array.isArray(cellTooltip) ? (
                  <div>
                    {cellTooltip?.document_section && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong>Document Section:</strong>{' '}
                        {cellTooltip?.document_section || '-'}
                      </div>
                    )}
                    {cellTooltip?.excerpt && (
                      <div>
                        <strong>Extract:</strong> {cellTooltip?.excerpt || '-'}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <strong>Extract:</strong> {cellTooltip || '-'}
                  </div>
                )
              }
            >
              {tableCell}
            </Tooltip>
          ) : (
            tableCell
          );
        })}
      </TableRow>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      {showAddMore && <AddButton />}
      <TableContainer component={Paper} className={classes.paper}>
        <Table>
          <TableHead>
            <TableRow className={classes.headerRow}>
              {visibleColumns?.map((column, index) => (
                <TableCell
                  key={index}
                  className={classes.headerCellStyles}
                  align={column?.align}
                >
                  {column.isSortEnabled ? (
                    <TableSortLabel
                      active={sortConfig?.fieldName === column?.accessor}
                      direction={
                        sortConfig?.fieldName === column?.accessor
                          ? sortConfig?.direction!
                          : 'asc'
                      }
                      onClick={() => handleSort(column?.accessor)}
                      title={sortConfig?.direction === 'asc' ? 'A-Z' : 'Z-A'}
                    >
                      {column.header}
                    </TableSortLabel>
                  ) : (
                    column.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {groupByCategory && groupedRows
              ? Object.entries(groupedRows).map(
                  ([paramCategory, categoryGroups]: any) => (
                    <React.Fragment key={paramCategory}>
                      <TableRow className={classes.categoryRow}>
                        <TableCell
                          colSpan={visibleColumns.length}
                          className={classes.categoryCell}
                        >
                          {paramCategory}
                        </TableCell>
                      </TableRow>

                      {Object.entries(categoryGroups).map(
                        ([category, rows]: any) => (
                          <React.Fragment key={category}>
                            <TableRow className={classes.clauseRow}>
                              <TableCell
                                colSpan={visibleColumns.length}
                                className={classes.clauseCell}
                              >
                                {category}
                              </TableCell>
                            </TableRow>
                            {rows.map((row: any, i: number) =>
                              renderRow(
                                row,
                                `${paramCategory}-${category}-${i}`,
                              ),
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </React.Fragment>
                  ),
                )
              : rowsData?.map((row, i) => renderRow(row, String(i)))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MatrixTable;
